/**
 * @fileoverview Progress (C7, decided Nov 1, 2026): the student's streak and
 * the shape of their practice, time and milestones, and the latest logs. What
 * used to be Portfolio, the public page, is an action here: Share opens
 * "Share your progress" (/student/progress/share). The web twin of
 * mobile/src/screens/StudentProgressScreen.js.
 *
 * Read on every visit and after every log made in the portal.
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StudentAuthContext } from "@/App";
import { getPracticeStats, listPracticeLogs } from "@/lib/studentApi";
import { practiceLogsEnabled } from "@/lib/featureFlags";
import { localDay } from "@/lib/studentPractice";
import { Button, Card, Icon, SectionHeader, StatCard } from "@/components/studio/primitives";
import { SEmpty, SHeader } from "@/components/studio/studentUi";
import { StudentPracticeStreak } from "@/components/StudentPracticeStreak";
import { useStudentPractice } from "@/components/StudentPracticeContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Today", "Yesterday", else "Oct 30", on the student's own calendar. */
export function logDay(day, now = new Date()) {
  if (day === localDay(0, now)) return "Today";
  if (day === localDay(-1, now)) return "Yesterday";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || "");
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : "";
}

/** "8.5 h": time practiced, to a tenth of an hour. */
const hours = (minutes) => `${Math.round((Number(minutes) || 0) / 6) / 10} h`;

const StudentProgressPage = () => {
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const { version, openLog } = useStudentPractice();
  const tracking = practiceLogsEnabled(student);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(tracking);

  const load = useCallback(
    async (signal) => {
      if (!tracking) return;
      try {
        const [statsData, logsData] = await Promise.all([getPracticeStats({ signal }), listPracticeLogs({ signal })]);
        setStats(statsData);
        setLogs(logsData);
        // A milestone is celebrated once, by whichever read first notices it.
        (statsData?.newly_awarded || []).forEach((m) => toast.success(m.title));
      } catch (err) {
        if (!signal?.aborted) console.warn("Failed to load progress:", err);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [tracking],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, version]);

  const share = () => navigate("/student/progress/share");
  const milestones = stats?.milestones || [];
  const recent = [...logs].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))).slice(0, 3);

  return (
    <div className="mx-auto max-w-[1080px] px-6 pb-32 pt-11 md:px-12" data-testid="student-progress">
      <SHeader
        eyebrow="Student portal"
        title="Progress"
        action={
          <Button variant="ghost" icon="share" onPress={share} data-testid="progress-share">
            Share
          </Button>
        }
      />

      {!tracking ? (
        <Card data-testid="progress-off">
          <p className="font-sans text-base font-semibold text-[var(--text-primary)]">Practice tracking is off</p>
          <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">Nothing you have already logged has been deleted.</p>
        </Card>
      ) : loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <div key={i} className="skeleton h-[160px] rounded-[20px]" />)}
        </div>
      ) : (
        <>
          <StudentPracticeStreak stats={stats} />

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <StatCard value={hours(stats?.total_minutes)} label="practiced" sub="all time" data-testid="progress-hours" />
            <StatCard value={milestones.length} label={milestones.length === 1 ? "milestone" : "milestones"} sub="earned so far" />
          </div>

          {milestones.length > 0 && (
            <Card className="mt-6" data-testid="progress-milestones">
              <SectionHeader>Milestones</SectionHeader>
              <ul className="space-y-3">
                {milestones.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 font-sans text-[15px] text-[var(--text-primary)]">
                    <Icon name="award" size={18} className="text-[var(--accent-yellow-text)]" />
                    {m.title}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="mt-8">
            <SectionHeader
              action={logs.length ? "See all →" : undefined}
              onAction={() => navigate("/student/rehearsals")}
              actionTestId="progress-see-all"
            >
              Practice log
            </SectionHeader>
            {recent.length ? (
              <Card padding={6} data-testid="progress-log">
                {recent.map((log, i) => (
                  <div
                    key={log.log_id ?? log.id ?? i}
                    className={[
                      "flex items-center justify-between gap-4 px-4 py-3",
                      i < recent.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                    ].join(" ")}
                    data-testid={`progress-log-${i}`}
                  >
                    <div className="min-w-0">
                      <div className="font-sans text-[15px] font-semibold text-[var(--text-primary)]">{log.duration_minutes} min</div>
                      {log.practice_item || log.notes ? (
                        <div className="truncate font-sans text-[13px] text-[var(--text-tertiary)]">{log.practice_item || log.notes}</div>
                      ) : null}
                    </div>
                    <div className="shrink-0 font-sans text-[13px] text-[var(--text-tertiary)]">{logDay(log.date)}</div>
                  </div>
                ))}
              </Card>
            ) : (
              <Card padding={0}>
                <SEmpty
                  icon="clock"
                  title="No practice logged yet"
                  body="Log a session and your streak starts here."
                  cta="Log practice"
                  ctaIcon="plus"
                  onCta={() => openLog()}
                />
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentProgressPage;
